import React, { useEffect, useMemo, useState } from 'react';
import { IImageRepositoryItem, IImageRepositoryItemGroup } from './types';
import ImageRepositoryListItem from './ImageRepositoryListItem';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import { Collapse } from 'react-collapse';
import clsx from 'clsx';
import ImageRepositoryListItemGroupHeader from './ImageRepositoryListItemGroupHeader';
import Checkbox from '../Inputs/Checkbox';

interface ImageRepositoryListItemGroupProps {
  group: IImageRepositoryItemGroup;
  selectedItems: IImageRepositoryItem[];
  onItemSelect: (selected: boolean, item: IImageRepositoryItem) => void;
}

const ImageRepositoryListItemGroup = ({
  group,
  selectedItems,
  onItemSelect,
}: ImageRepositoryListItemGroupProps) => {
  const [collapsibleOpen, setCollapsibleOpen] = useState(true);
  const hasAnyItem = useMemo(
    () => group.items.length > 0,
    [group.items.length]
  );
  const hasAllInGroupSelected = useMemo(
    () =>
      hasAnyItem &&
      group.items.every(
        (item) =>
          selectedItems.findIndex((x) => x.systemId == item.systemId) !==
          -1
      ),
    [group.items, hasAnyItem, selectedItems]
  );
  const onSelectGroupClick = () => {
    if (hasAllInGroupSelected) {
      selectedItems.forEach((item) => onItemSelect(false, item));
    } else {
      group.items.forEach((item) => onItemSelect(true, item));
    }
  };

  useEffect(() => {
    setCollapsibleOpen(!hasAllInGroupSelected);
  }, [hasAllInGroupSelected]);

  return (
    <div>
      <div className="flex justify-between rounded-t bg-gray-800 px-3 py-2 text-white">
        <button
          className="flex items-center gap-2"
          onClick={() => setCollapsibleOpen(!collapsibleOpen)}
        >
          <Icon
            name="caret-down"
            className={clsx(
              'h-6 w-6 transition-transform',
              collapsibleOpen ? 'rotate-180' : 'rotate-0'
            )}
          />
          <h6 className="text-md font-normal">{group.articleNumber}</h6>
        </button>
        {hasAnyItem && (
          <div className="flex items-center gap-4">
            <div className="flex cursor-pointer items-center gap-2">
              <label
                htmlFor={`select-all-${group.articleNumber}`}
                className="text-md"
              >
                {translate('general.select-all')}
              </label>
              <Checkbox
                id={`select-all-${group.articleNumber}`}
                type="checkbox"
                checked={hasAllInGroupSelected}
                onChange={() => onSelectGroupClick()}
              />
            </div>
          </div>
        )}
      </div>
      <div>
        <Collapse isOpened={collapsibleOpen}>
          <div className="flex flex-col gap-2 rounded-b border border-t-0 border-solid border-gray-900 bg-white px-3 py-2 text-md">
            <small>{group.name}</small>
            {hasAnyItem && (
              <>
                <ImageRepositoryListItemGroupHeader />
                {group.items.map((item, idx) => (
                  <ImageRepositoryListItem
                    item={item}
                    onSelect={(selected) => onItemSelect(selected, item)}
                    selected={
                      selectedItems.findIndex(
                        (x) => x.systemId == item.systemId
                      ) !== -1
                    }
                    key={`${group.articleNumber}-${item.name}-${idx}-list-item`}
                  />
                ))}
              </>
            )}
            {!hasAnyItem && (
              <h6 className="flex items-center justify-center text-md">
                {translate('image-repository.no-items')}
              </h6>
            )}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default ImageRepositoryListItemGroup;
